import {Component, OnInit} from '@angular/core';
import {DatePipe} from '@angular/common';
import {ActivatedRoute} from '@angular/router';
import {plainToClass} from 'class-transformer';
import {ConfirmationService, MenuItem, SelectItem} from 'primeng/api';

import {GrowlService, UserService} from "@app/core";
import {BreadcrumbMain, BreadcrumbOrders, BreadcrumbProcurement, YearRange} from "../../classes/common";
import {LastDays, OrgType, TradingPartnerSummary, TradingPartnerTabIndex, TradingPartnerTransactionSummary} from '../../classes/trading-partner';
import {RouteLink} from '../../enums/route-link.enum';
import {Transaction} from '../../enums/txn-type.enum';
import {Format} from '../../enums/format.enum';
import {EnumType} from '../../enums/enum-type.enum';
import {CommonTransformationService} from '../../services/common-transformation.service';
import {CommonDataService} from '../../services/common-data.service';
import {CurrentTxnStatus} from '@app/shared/classes/current-txn-status';
import {Validation} from '@app/shared/enums/validation.enum';
import {TradingPartnerService} from '@app/shared/services/trading-partner.service';
import {GrowlType, PrimeNgConfig, PrimeNgSupportService, TableColumn} from "@app/shared";

@Component({
  selector: 'app-org-summary',
  templateUrl: './org-summary.component.html',
  styleUrls: ['./org-summary.component.css'],
  providers: [DatePipe, ConfirmationService]
})
export class OrgSummaryComponent implements OnInit {

  // standard format and validation
  Format: typeof Format = Format;
  Transaction: typeof Transaction = Transaction;
  OrgType: typeof OrgType = OrgType;
  PrimeNgConfig = PrimeNgConfig;
  RouteLink: typeof RouteLink = RouteLink;
  helpVendorsOverview: boolean = false;
  helpVendorsAdd: boolean = false;
  helpVendorsManage: boolean = false;
  helpVendorsTransactions: boolean = false;
  helpCustomersOverview: boolean = false;
  helpCustomersSetup: boolean = false;
  helpCustomersContacts: boolean = false;
  helpCustomersPayments: boolean = false;
  helpCustomersSalesTax: boolean = false;
  TradingPartnerTabIndex: typeof TradingPartnerTabIndex = TradingPartnerTabIndex;
  Validation: typeof Validation = Validation;

  YearRange = YearRange;

  breadcrumb: MenuItem[] = [];

  orgType: OrgType;
  transaction: Transaction;

  partnerSummaries: TradingPartnerSummary[];
  partnerTableColumns: TableColumn[];

  transactionSummaries: TradingPartnerTransactionSummary[];
  transactionTableColumns: TableColumn[];

  tabIndex: number;

  // transaction search related properties
  showSearch: boolean = false;
  searchTxn: SearchTransactionSummary;
  searchTxnAutoCompleteOptions: SearchTransactionSummary[];
  startingDate: string;
  endingDate: string;
  partnerId: number;
  businessName: string;
  txnType: string;
  disabledEndDate: boolean = true;
  txnTypeDropDown: SelectItem[];
  daysDropDown: SelectItem[];
  viewOptions: SelectItem[] = [{label: "Active", value: true}, {label: "All", value: false}];
  viewOptionsactive: SelectItem[] = [{label: "Active", value: true}, {label: "All", value: false}];
  getActivePartners: boolean = true;
  getActiveTransactions: boolean = true;

  constructor(private route: ActivatedRoute, private commonDataService: CommonDataService, private confirmationService: ConfirmationService, private datePipe: DatePipe,
              private commonTransformationService: CommonTransformationService, private dataService: TradingPartnerService, private user: UserService, private growl: GrowlService,
              private primeNgService: PrimeNgSupportService) {
  }

  ngOnInit() {
    this.searchTxn = new SearchTransactionSummary();
    this.orgType = <OrgType>this.route.snapshot.params['partnerType'];

    if (this.orgType == OrgType.Customer) {
      this.transaction = Transaction.Customer;
    } else if (this.orgType == OrgType.Vendor) {
      this.transaction = Transaction.Vendor;
    }

    this.buildMenu();
    this.initTableColumns();
    this.getTradingPartnerSummary(this.orgType, this.getActivePartners);    // get active trading partners
    this.loadTxnTypes();
    this.getDaysDropdown();
  }

  buildMenu(): void {
    this.breadcrumb.push(BreadcrumbMain);
    if (this.orgType === OrgType.Customer) {
      this.breadcrumb.push(BreadcrumbOrders);
      this.breadcrumb.push({label: 'Customer Summary', routerLink: ''});
    } else if (this.orgType === OrgType.Vendor) {
      this.breadcrumb.push(BreadcrumbProcurement);
      this.breadcrumb.push({label: 'Vendor Summary', routerLink: ''});
    }
  }

  //search panel toggle
  toggleSearch(): void {
    this.showSearch = !this.showSearch;
  }

  onChangeTab(index: number): void {
    this.tabIndex = index;
    if (this.tabIndex == OrgSummaryTab.Transactions && !this.transactionSummaries) {
      this.getTransactionSummary(this.orgType, this.getActiveTransactions);
    }
  }

  /**
   * @desc: handle change in input switch Active and Inactive Partners
   */
  onChangeActivePartners(activePartner: boolean): void {
    this.getTradingPartnerSummary(this.orgType, activePartner);
  }

  /**
   * @desc: handle change in input switch Active and Inactive Partner's Transactions
   */
  onChangeActiveTransaction(activeTransaction: boolean): void {
    this.getTransactionSummary(this.orgType, activeTransaction);
  }

  onSelectStartingDate(date: string): void {
    this.startingDate = this.datePipe.transform(new Date(Date.parse(date)), Format.DataserviceDate);       //commented code for replacing string with a date
    this.disabledEndDate = false;
  }

  onSelectEndingDate(date: string): void {
    this.endingDate = this.datePipe.transform(new Date(Date.parse(date)), Format.DataserviceDate);                //commented code for replacing string with a date
    if (this.endingDate < this.startingDate) {
      this.growl.add(GrowlType.Warn, 'Ending date cannot be before Starting date. By default Todays date is assigned to Ending date');
      this.endingDate = this.datePipe.transform(new Date(), Format.DataserviceDate);
    }
  }

  onClickSearchTransaction(): void {
    if (this.businessName) {
      this.searchTxn.businessName = this.businessName;
    }
    if (this.startingDate && !this.endingDate) {
      this.endingDate = this.datePipe.transform(new Date(), Format.DataserviceDate);           //commented code for replacing string with a date
      this.searchTxn.dateRange = this.startingDate + ',' + this.endingDate;
    }
    if (this.startingDate && this.endingDate) {
      this.searchTxn.dateRange = this.startingDate + ',' + this.endingDate;
    }
    this.dataService.searchTransaction(this.orgType, this.searchTxn).subscribe(res => {
      this.transactionSummaries = plainToClass(TradingPartnerTransactionSummary, res.list);
    });
  }

  onClickClearSearchTransaction(): void {
    this.searchTxn = new SearchTransactionSummary();
    this.disabledEndDate = true;
    this.startingDate = null;
    this.endingDate = null;
  }

  searchPartner(name: string): void {
    this.dataService.searchPartnerSummary(name, null, null, this.orgType).subscribe(res => {
      this.searchTxnAutoCompleteOptions = plainToClass(SearchTransactionSummary, res.list);
    });
  }

  onSelectBusinessName(): void {
    this.businessName = this.searchTxn.businessName;
    this.dataService.searchPartnerTransSummary(this.businessName, this.orgType).subscribe(res => {
      this.searchTxnAutoCompleteOptions = plainToClass(SearchTransactionSummary, res.list);
    });
  }

  onClickDeletePartner(partner: TradingPartnerSummary): void {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete  ' + partner.businessName + ' ?',
      header: 'Delete Partner Confirmation',
      accept: () => {
        this.dataService.deleteTradingPartner(partner.id).subscribe(res => {
          this.partnerSummaries = this.primeNgService.deleteById(this.partnerSummaries, partner.id);
          this.growl.add(GrowlType.Success, ' Partner deleted.');
        });
      },
      reject: () => {
      }
    });
  }

  openPOPdf(transaction: TradingPartnerTransactionSummary): void {
    this.dataService.getTxnPdf(transaction.transactionType, transaction.refID).subscribe(res => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
      const blob = res.body;
      let objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl);
    });
  }

  private initTableColumns(): void {

    this.partnerTableColumns = [
      {field: 'businessName', header: 'Company'},
      {field: 'txnNumber', header: 'Transaction #'},
      {field: 'active', header: 'Active'},
      {field: 'currentStatus', header: 'Status'},
      {field: 'fullName', header: 'Contact Name'},
      {field: 'title', header: 'Contact Title'},
      {field: 'action', header: 'Action'},
    ];

    this.transactionTableColumns = [
      {field: 'transactionType', header: 'Transaction Type'},
      {field: 'businessName', header: 'Vendors'},
      {field: 'active', header: 'Vendor Status'},
      {field: 'docNumber', header: 'Transaction'},
      {field: 'date', header: 'Date'},
      {field: 'uniqueChargeCodes', header: 'Charge Code'},
      {field: 'amount', header: 'Amount'},
    ];
  }

  private getDaysDropdown() {
    this.daysDropDown = [];
    for (let l in LastDays) {
      if (Number(l)) {
        this.daysDropDown.push({label: l, value: Number(l)});
      }
    }
    this.daysDropDown = [... this.daysDropDown];
  }

  private loadTxnTypes(): void {
    this.searchTxnAutoCompleteOptions = [];
    this.dataService.getTransactionTypes(EnumType.TradingPartnerTxnType, this.orgType).subscribe(res => {
      this.txnTypeDropDown = res;
    });

  }

  /**
   * @desc: summary line items of customer/vendor when page is routed and loaded
   */
  private getTradingPartnerSummary(orgType: OrgType, active?: boolean): void {
    this.dataService.getTradingPartnerSummary(orgType, active).subscribe(res => {
      this.partnerSummaries = plainToClass(TradingPartnerSummary, res.list);
      this.partnerSummaries.forEach(ps => {
        ps.currentTxnStatus = new CurrentTxnStatus(ps.currentStatus);
      });
    });
  }

  /**
   * @desc transaction line items of customer/vendor when page is routed and loaded
   */
  private getTransactionSummary(orgType: OrgType, active?: boolean): void {
    this.dataService.getTransactionSummary(orgType, active).subscribe(res => {
      this.transactionSummaries = plainToClass(TradingPartnerTransactionSummary, res.list);
    });
  }
}

export class SearchTransactionSummary {
  transactionType: string;
  businessName: string;
  dateRange: string;
  numberOfDays: number;
}

export enum OrgSummaryTab {
  Manage,
  Transactions
}
